import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function TrelloCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const connectTrello = async () => {
      // Get token from URL fragment (hash)
      const hash = window.location.hash.substring(1);
      const hashParams = new URLSearchParams(hash);
      const token = hashParams.get('token');

      // Get state from query string
      const state = searchParams.get('state');

      if (!token) {
        setStatus('error');
        setErrorMessage('Missing token from Trello');
        setTimeout(() => {
          navigate('/dashboard/integrations?error=Missing%20token');
        }, 2000);
        return;
      }

      if (!state) {
        setStatus('error');
        setErrorMessage('Missing state parameter');
        setTimeout(() => {
          navigate('/dashboard/integrations?error=Missing%20state');
        }, 2000);
        return;
      }

      try {
        const response = await fetch('/api/integrations/callback/trello/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token, state }),
        });

        const data = (await response.json()) as { success: boolean; error?: string };

        if (data.success) {
          setStatus('success');
          setTimeout(() => {
            navigate('/dashboard/integrations?success=true&provider=trello');
          }, 1500);
        } else {
          setStatus('error');
          setErrorMessage(data.error || 'Connection failed');
          setTimeout(() => {
            navigate(`/dashboard/integrations?error=${encodeURIComponent(data.error || 'Connection failed')}`);
          }, 2000);
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Connection failed');
        setTimeout(() => {
          navigate('/dashboard/integrations?error=Connection%20failed');
        }, 2000);
      }
    };

    connectTrello();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center">
      <div className="text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-accent-purple mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-white mb-2">Connecting Trello...</h2>
            <p className="text-text-secondary">Please wait while we complete the connection.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Trello Connected!</h2>
            <p className="text-text-secondary">Redirecting to integrations...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Connection Failed</h2>
            <p className="text-text-secondary">{errorMessage}</p>
          </>
        )}
      </div>
    </div>
  );
}
